import { FC } from 'react';
import classNames from 'classnames';

import {
  useFilteredSliceItemsByLocationSlug,
  useScreenWidth,
  useShowByLocationSlug,
} from '@/hooks';
import { Container } from '@/components';
import { getSlicePadding, HeadingAsParagraph } from '@/utils';
import { ImageGallerySlice } from '@/types';
import {
  CTAButton,
  GalleryVariation,
  getGalleryContentAlign,
  hasContentAlignField,
  renderTextSlice,
} from './ImageGallery.components';

import style from './image_gallery.module.scss';

const ImageGallery: FC<ImageGallerySlice> = slice => {
  const { width } = useScreenWidth();
  const { primary, variation } = slice;
  const {
    heading,
    heading_color,
    title,
    title_color,
    description,
    description_color,
    bottom_spacing,
    effect_zoom_images,
    border_section,
    background_color,
    max_header_width,
    padding_section,
  } = primary;

  const { showContent } = useShowByLocationSlug(slice?.primary?.location_slug);
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);

  const borderTop = border_section ?? '';
  const borderBottom = border_section ?? '';
  const backgroundImage = slice?.primary?.background_image?.url;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;
  const maxWidtHeader =
    primary?.max_header_width && width > 428
      ? `${primary?.max_header_width}px`
      : '100%';

  const getMaxWidthdescription = () => {
    if (width < 428) {
      return '100%';
    }

    if (variation === 'imageCorousel' && primary?.max_width_description) {
      return `${primary?.max_width_description}px`;
    }

    const maxWidth = max_header_width ? `${max_header_width}px` : '100%';

    return maxWidth;
  };

  if (!showContent || !filteredItems?.length || !slice?.primary?.active) {
    return null;
  }

  const hasContentAlign = hasContentAlignField(variation);
  const contentAlign =
    getGalleryContentAlign(
      variation,
      (primary as { content_align?: string })?.content_align,
    ) ?? 'center';

  return (
    <section
      className={classNames(
        style.image_gallery,
        getSlicePadding(padding_section),
        {
          ['spacing_bottom']: bottom_spacing,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        backgroundColor: background_color ?? '',
        borderTop,
        borderBottom,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Container
        className={classNames(style.image_gallery_container, {
          [style.carousel_container]: variation === 'imageCorousel',
          [style.container__effect_img]: effect_zoom_images,
          [style[`image_gallery_container_align_${contentAlign}`]]:
            hasContentAlign,
        })}
      >
        <HeadingAsParagraph
          className={style.gallery_heading}
          color={heading_color!}
          field={heading}
          style={{
            maxWidth: maxWidtHeader,
            textAlign: contentAlign,
          }}
        />
        {renderTextSlice(title, title_color ?? '', maxWidtHeader, contentAlign)}
        {renderTextSlice(
          description,
          description_color ?? '',
          getMaxWidthdescription(),
          contentAlign,
        )}
        <GalleryVariation slice={{ ...slice, items: filteredItems as any }} />
        <CTAButton {...slice} />
      </Container>
    </section>
  );
};

export { ImageGallery };
